import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import { useToast } from "./Toast";
import { Button, Input, Select, Textarea } from "./ui";
import { cn } from "../lib/cn";
import { formatChars } from "../lib/evalClusterPlan";
import {
  missingTokens,
  previewSynthesisPrompt,
  rollbackSynthesisPrompt,
  saveSynthesisPrompt,
  type SynthesisPromptPreview,
  type SynthesisPromptTemplate,
} from "../lib/synthesisPromptsApi";
import { formatApiError } from "../lib/utils";

type Props = {
  /** production: edits are drafts saved as new versions. sandbox: edits stay in this browser. */
  mode: "production" | "sandbox";
  templates: SynthesisPromptTemplate[];
  loading?: boolean;
  onTemplatesChange: (templates: SynthesisPromptTemplate[]) => void;
  /** Sandbox mode: this browser's edits by kind (the eval playground sends them with a run). */
  sandbox?: Record<string, string>;
  onSandboxChange?: (next: Record<string, string>) => void;
  /** Target the preview resolves the master prompt's company placeholders for. */
  targetCompany?: string;
  pdrProfile?: string;
  className?: string;
};

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function SynthesisPromptEditor({
  mode,
  templates,
  loading = false,
  onTemplatesChange,
  sandbox,
  onSandboxChange,
  targetCompany = "",
  pdrProfile,
  className,
}: Props) {
  const { toast } = useToast();
  const [activeKind, setActiveKind] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [versionId, setVersionId] = useState("");
  const [preview, setPreview] = useState<SynthesisPromptPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!activeKind && templates[0]) setActiveKind(templates[0].kind);
  }, [templates, activeKind]);

  const active = templates.find((t) => t.kind === activeKind) ?? templates[0];
  const edits = mode === "sandbox" ? (sandbox ?? {}) : drafts;
  const contentOf = (kind: string) => templates.find((t) => t.kind === kind)?.content ?? "";
  const modified = (kind: string) => edits[kind] !== undefined && edits[kind] !== contentOf(kind);
  const text = active ? (edits[active.kind] ?? active.content) : "";
  const missing = active ? missingTokens(text, active.required) : [];
  const editedCount = templates.filter((t) => modified(t.kind)).length;

  const writeEdits = (next: Record<string, string>) => {
    if (mode === "sandbox") onSandboxChange?.(next);
    else setDrafts(next);
    setPreview(null);
    setPreviewOpen(false);
  };

  const setText = (value: string) => {
    if (!active) return;
    const next = { ...edits };
    if (value === active.content) delete next[active.kind];
    else next[active.kind] = value;
    writeEdits(next);
  };

  const dropEdit = (kind: string) => {
    const next = { ...edits };
    delete next[kind];
    writeEdits(next);
  };

  const save = async () => {
    if (!active || missing.length) return;
    if (
      mode === "sandbox" &&
      !window.confirm(
        `Save "${active.label}" to production? Every Vendor Clusters synthesis will use it from now on.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const next = await saveSynthesisPrompt(
        active.kind,
        text,
        note.trim() || (mode === "sandbox" ? "from the eval playground" : ""),
      );
      onTemplatesChange(next);
      dropEdit(active.kind);
      setNote("");
      toast(`${active.label} saved to production`);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!active || !versionId) return;
    if (!window.confirm(`Restore this version of "${active.label}" to production?`)) return;
    setBusy(true);
    try {
      onTemplatesChange(await rollbackSynthesisPrompt(versionId));
      dropEdit(active.kind);
      setVersionId("");
      toast(`${active.label} restored`);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      setPreview(
        await previewSynthesisPrompt({
          target_company: targetCompany,
          prompts: edits,
          pdr_schema_profile: pdrProfile,
        }),
      );
    } catch (e) {
      toast(formatApiError(e), true);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!active) {
    return (
      <p className="px-5 py-3 text-body-sm text-on-surface-variant">
        {loading ? "Loading prompts…" : "No prompts found."}
      </p>
    );
  }

  const isModified = modified(active.kind);
  const status =
    mode === "sandbox"
      ? isModified
        ? "Edited in this browser: your runs use it, production does not"
        : "Same as production"
      : isModified
        ? "Unsaved draft"
        : active.uses_workspace_copy
          ? "Operator-edited version"
          : "Shipped default";

  return (
    <div className={cn("flex flex-col min-h-0", className)}>
      <div
        className="flex flex-wrap gap-1 px-5 pt-3 border-b border-outline-variant"
        role="tablist"
        aria-label="Synthesis prompt parts"
      >
        {templates.map((t) => (
          <button
            key={t.kind}
            type="button"
            role="tab"
            aria-selected={t.kind === active.kind}
            onClick={() => {
              setActiveKind(t.kind);
              setVersionId("");
            }}
            className={cn(
              "px-3 py-1.5 text-[11px] font-data-label uppercase tracking-wide rounded-t border border-b-0",
              t.kind === active.kind
                ? "bg-surface-container text-primary border-outline-variant"
                : "text-on-surface-variant border-transparent hover:bg-surface-container-high/60",
            )}
          >
            {t.label}
            {modified(t.kind) ? (
              <span className="ml-1 text-tertiary" title="Edited">
                ●
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-2 px-5 py-3 overflow-y-auto custom-scrollbar">
        <p className="text-body-sm text-on-surface-variant">{active.help}</p>
        <p className="text-[11px] font-data-mono text-on-surface-variant flex flex-wrap gap-x-3 gap-y-1">
          <span>
            Must keep:{" "}
            {active.required.map((token) => (
              <span
                key={token}
                className={cn("mr-1", missing.includes(token) ? "text-error font-bold" : "text-primary")}
              >
                {token}
              </span>
            ))}
          </span>
          <span className={isModified ? "text-tertiary" : undefined}>{status}</span>
          {editedCount > 1 ? <span className="text-tertiary">{editedCount} parts edited</span> : null}
        </p>
        {missing.length ? (
          <p className="text-[11px] text-error">
            Put back {missing.join(", ")}: without it this part cannot be saved or sent.
          </p>
        ) : null}
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          aria-label={active.label}
          className="flex-1 min-h-[16rem] text-[12px] leading-relaxed"
        />

        <div className="flex flex-wrap items-center gap-2">
          {mode === "production" ? (
            <>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note for this version (optional)"
                className="max-w-xs"
              />
              <Button
                size="sm"
                variant="primary"
                disabled={busy || !isModified || missing.length > 0}
                onClick={() => void save()}
              >
                Save version
              </Button>
              {isModified ? (
                <Button size="sm" variant="ghost" onClick={() => dropEdit(active.kind)}>
                  Discard draft
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={busy || !isModified || missing.length > 0}
                onClick={() => void save()}
                title="Make this text what every Vendor Clusters synthesis sends"
              >
                Save to production
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!isModified}
                onClick={() => dropEdit(active.kind)}
              >
                Reset to production
              </Button>
            </>
          )}
          <span className="ml-auto" />
          {mode === "production" && active.versions.length ? (
            <>
              <Select
                value={versionId}
                onChange={(e) => setVersionId(e.target.value)}
                className="w-auto max-w-[18rem] text-[11px]"
                aria-label="Version history"
              >
                <option value="">History ({active.versions.length})…</option>
                {active.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {formatWhen(v.saved_at)}
                    {v.note ? ` · ${v.note}` : ""}
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="ghost" disabled={!versionId || busy} onClick={() => void restore()}>
                Restore
              </Button>
            </>
          ) : null}
          <Button
            size="sm"
            variant="link"
            onClick={() => (previewOpen ? setPreviewOpen(false) : void openPreview())}
            className="inline-flex items-center gap-1"
          >
            <Icon name="visibility" className="text-base" />
            {previewOpen ? "Hide preview" : "Preview what the model receives"}
          </Button>
        </div>

        {previewOpen ? (
          <div className="rounded border border-outline-variant bg-surface-container-low">
            {previewLoading || !preview ? (
              <p className="p-3 text-body-sm text-on-surface-variant">Rendering…</p>
            ) : (
              <>
                <p className="px-3 py-2 text-[11px] text-on-surface-variant border-b border-outline-variant">
                  For {targetCompany || "the desk's target company"}
                  {editedCount ? " · with your edits" : ""} · the {preview.profile} PDR reference (
                  {formatChars(preview.pdr_reference_chars)} chars) is sent in full; the evidence and the
                  current PDR are added on each call.
                </p>
                {(
                  [
                    ["System message", preview.system],
                    ["New project instructions", preview.new_project],
                    ["Update instructions", preview.update],
                  ] as const
                ).map(([label, body]) => (
                  <details
                    key={label}
                    open={label === "System message"}
                    className="border-b border-outline-variant last:border-b-0"
                  >
                    <summary className="px-3 py-2 cursor-pointer text-[11px] font-data-label uppercase tracking-wide text-on-surface-variant">
                      {label} · {formatChars(body.length)} chars
                    </summary>
                    <pre className="px-3 pb-3 whitespace-pre-wrap text-[11px] font-data-mono text-on-surface/90 max-h-72 overflow-y-auto custom-scrollbar">
                      {body}
                    </pre>
                  </details>
                ))}
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
